import { useTranslation } from "react-i18next";
import {
    Button,
    Field,
    FieldDescription,
    FieldLabel,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
    Text,
    Textarea,
} from "@athena/ui";
import { cn } from "@athena/ui/utils";
import { Check, CheckCircle2, Copy, RotateCw } from "lucide-react";
import { TagPicker, type TagOption } from "./TagPicker";
import {
    canEditDetails,
    isTransferred,
    isUploaded,
    missingDetails,
    type QueueDetails,
    type QueueItem,
} from "./uploadQueue";

export interface PresenterOption {
    id: string;
    name: string;
    title?: string | null;
}

/** A brand that can be chosen — never one that already sees everything, which would mean nothing. */
export interface BrandOption {
    id: string;
    slug: string;
    name: string;
}

interface VideoDetailsFormProps {
    item: QueueItem;
    presenters: PresenterOption[];
    tags: TagOption[];
    /** Creates a tag; omitted for anyone who may not, which hides the option. See `TagPicker`. */
    onCreateTag?: (name: string) => Promise<TagOption | undefined>;
    brands: BrandOption[];
    onEdit: (patch: Partial<QueueDetails>) => void;
    onFinish: () => void;
    /** How many other videos would receive this one's presenter and tags. */
    copyTargets: number;
    onCopy: () => void;
    /** Whether Finish moves on to the next video needing details. */
    hasNext: boolean;
    onRetry: () => void;
}

/**
 * Step two for one video — title, description, presenter and tags — shown inside its queue row
 * when the row is opened.
 *
 * Editable while the video is still uploading — the point of the queue is not to wait. Finish is
 * offered once nothing required is missing; pressed before the bytes are in, it waits and finishes on
 * its own (`finishWhenUploaded`).
 */
export function VideoDetailsForm({
    item,
    presenters,
    tags,
    onCreateTag,
    brands,
    onEdit,
    onFinish,
    copyTargets,
    onCopy,
    hasNext,
    onRetry,
}: VideoDetailsFormProps) {
    const { t } = useTranslation();
    const editable = canEditDetails(item);
    const missing = missingDetails(item);
    const ready = missing.length === 0;
    const details = item.details;
    const fieldId = (name: string) => `${item.key}-${name}`;
    const toggleBrand = (slug: string) =>
        onEdit({
            brands: details.brands.includes(slug)
                ? details.brands.filter((s) => s !== slug)
                : [
                      ...details.brands,
                      slug,
                  ],
        });

    return (
        <div className="flex flex-col gap-4">
            {!editable && (
                <div
                    className={cn(
                        "flex items-start gap-3 rounded-xl p-3",
                        item.published ? "bg-success/10" : "bg-destructive/10",
                    )}
                >
                    {item.published ? (
                        <CheckCircle2 className="text-success mt-0.5 h-5 w-5 shrink-0" />
                    ) : (
                        <RotateCw className="text-destructive mt-0.5 h-5 w-5 shrink-0" />
                    )}
                    <div className="flex flex-col gap-2">
                        <Text size="sm">
                            {item.published
                                ? isUploaded(item)
                                    ? t("admin.bulkUpload.doneNote")
                                    : t("admin.bulkUpload.savedNote")
                                : item.file
                                  ? t("admin.bulkUpload.failedNote")
                                  : t("admin.bulkUpload.recoveredNote")}
                        </Text>
                        {item.phase === "failed" && item.file && (
                            <Button size="sm" variant="outline" className="w-fit" onClick={onRetry}>
                                <RotateCw className="mr-1.5 h-3.5 w-3.5" />
                                {t("admin.bulkUpload.retry")}
                            </Button>
                        )}
                    </div>
                </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-4">
                    <Field>
                        <FieldLabel htmlFor={fieldId("title")} required>
                            {t("admin.bulkUpload.fieldTitle")}
                        </FieldLabel>
                        <Input
                            id={fieldId("title")}
                            value={details.title}
                            disabled={!editable}
                            maxLength={500}
                            onChange={(e) => onEdit({ title: e.target.value })}
                        />
                    </Field>
                    <Field>
                        <FieldLabel htmlFor={fieldId("description")} required>
                            {t("admin.bulkUpload.fieldDescription")}
                        </FieldLabel>
                        <Textarea
                            id={fieldId("description")}
                            value={details.description}
                            disabled={!editable}
                            maxLength={5000}
                            rows={4}
                            placeholder={t("admin.bulkUpload.descriptionPlaceholder")}
                            onChange={(e) => onEdit({ description: e.target.value })}
                        />
                    </Field>
                    {/*
                     * Not "18+": while birthdates are off nothing checks an age (ADR 0010) — a
                     * restricted lesson is hidden from whichever learners an administrator marked
                     * restricted, for whatever reason a partner has.
                     */}
                    {/*
                     * Named by reference rather than wrapped in a <label>: a label around the
                     * switch that also points at it forwards every click a second time, and the
                     * switch flipped straight back.
                     */}
                    <div className="border-border flex items-start justify-between gap-3 rounded-xl border p-3">
                        <span className="flex flex-col gap-0.5">
                            <Text as="span" size="sm" weight="semibold" id={fieldId("restricted-label")}>
                                {t("admin.bulkUpload.fieldRestricted")}
                            </Text>
                            <Text as="span" size="xs" muted id={fieldId("restricted-help")}>
                                {t("admin.bulkUpload.restrictedHelper")}
                            </Text>
                        </span>
                        {/*
                         * `primary` when on, overriding the shared switch's `secondary` here only, so
                         * it matches the tag and brand pills in this form.
                         */}
                        <Switch
                            className="data-[state=checked]:bg-primary"
                            aria-labelledby={fieldId("restricted-label")}
                            aria-describedby={fieldId("restricted-help")}
                            checked={details.restricted}
                            disabled={!editable}
                            onCheckedChange={(restricted) => onEdit({ restricted })}
                        />
                    </div>
                </div>

                <div className="flex flex-col gap-4">
                    <Field>
                        <FieldLabel htmlFor={fieldId("presenter")} required>
                            {t("admin.bulkUpload.fieldPresenter")}
                        </FieldLabel>
                        {presenters.length === 0 ? (
                            <Text size="sm" muted>
                                {t("admin.bulkUpload.noPresenters")}
                            </Text>
                        ) : (
                            <Select
                                value={details.presenterId ?? ""}
                                disabled={!editable}
                                onValueChange={(presenterId) => onEdit({ presenterId })}
                            >
                                <SelectTrigger id={fieldId("presenter")} className="w-full">
                                    <SelectValue placeholder={t("admin.bulkUpload.presenterPlaceholder")} />
                                </SelectTrigger>
                                <SelectContent>
                                    {presenters.map((p) => (
                                        <SelectItem key={p.id} value={p.id}>
                                            {p.name}
                                            {p.title && (
                                                <span className="text-muted-foreground ml-1.5">· {p.title}</span>
                                            )}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </Field>

                    <Field>
                        <FieldLabel htmlFor={fieldId("tags")} required>
                            {t("admin.bulkUpload.fieldTags")}
                        </FieldLabel>
                        <TagPicker
                            id={fieldId("tags")}
                            tags={tags}
                            selectedIds={details.tagIds}
                            onChange={(tagIds) => onEdit({ tagIds })}
                            disabled={!editable}
                            onCreate={onCreateTag}
                        />
                        <FieldDescription>{t("admin.bulkUpload.tagsHelper")}</FieldDescription>
                    </Field>

                    {brands.length > 0 && (
                        <Field>
                            <FieldLabel>{t("common.fields.brands")}</FieldLabel>
                            <FieldDescription>
                                {details.brands.length === 0
                                    ? t("admin.lms.brandsHelper")
                                    : t("admin.lms.brandDescription")}
                            </FieldDescription>
                            <div
                                role="group"
                                aria-label={t("common.fields.brands")}
                                className="flex flex-wrap gap-1.5 pt-1"
                            >
                                {brands.map((brand) => {
                                    const on = details.brands.includes(brand.slug);
                                    return (
                                        <button
                                            key={brand.id}
                                            type="button"
                                            aria-pressed={on}
                                            disabled={!editable}
                                            onClick={() => toggleBrand(brand.slug)}
                                            className={cn(
                                                "focus-visible:ring-ring inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60",
                                                // `primary`, like the tags and the Restricted switch beside them.
                                                on
                                                    ? "border-primary bg-primary text-primary-foreground"
                                                    : "border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground",
                                            )}
                                        >
                                            {on && <Check className="h-3 w-3" />}
                                            {brand.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </Field>
                    )}
                </div>
            </div>

            {editable && (
                <div className="border-border flex flex-wrap items-center justify-between gap-3 border-t pt-3">
                    <Text size="xs" muted className={cn(item.finishWhenUploaded && "text-primary")}>
                        {item.finishWhenUploaded
                            ? t("admin.bulkUpload.finishWaiting")
                            : !ready &&
                              t("admin.bulkUpload.missing", {
                                  fields: missing.map((f) => t(`admin.bulkUpload.field_${f}`)).join(", "),
                              })}
                    </Text>
                    <div className="flex flex-wrap items-center gap-2">
                        {/*
                         * A secondary action, so it reads as a link rather than a second shouting
                         * button. `max-w-none` because the shared Button caps itself at 280px and
                         * never wraps — this label ran underneath Finish.
                         */}
                        {copyTargets > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground hover:text-foreground w-auto max-w-none font-medium tracking-normal normal-case"
                                onClick={onCopy}
                                title={t("admin.bulkUpload.copyToOthersHint")}
                            >
                                <Copy className="mr-1.5 h-3.5 w-3.5" />
                                {t("admin.bulkUpload.copyToOthers", { count: copyTargets })}
                            </Button>
                        )}
                        <Button
                            variant="default"
                            className="w-auto max-w-none shrink-0"
                            disabled={!ready || item.finishWhenUploaded}
                            loading={item.publishing}
                            onClick={onFinish}
                        >
                            {!isTransferred(item)
                                ? t("admin.bulkUpload.finishAfterUpload")
                                : hasNext
                                  ? t("admin.bulkUpload.finishAndNext")
                                  : t("admin.bulkUpload.finish")}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
